import { useQuery } from "@tanstack/react-query";
import { HardDrive, Users, Building2 } from "lucide-react";
import { IntegrationsService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { cn } from "@/lib/utils";

/**
 * Discriminated union of the picker's possible sources. Mirrors the backend
 * `source` query parameter accepted by /integrations/gdrive/files:
 *   - { kind: "my_drive" }              → "my_drive"
 *   - { kind: "shared_with_me" }        → "shared_with_me"
 *   - { kind: "shared_drive", id, name } → "shared_drive:<id>"
 */
export type GoogleDriveSource =
  | { kind: "my_drive" }
  | { kind: "shared_with_me" }
  | { kind: "shared_drive"; id: string; name: string };

export const MY_DRIVE_SOURCE: GoogleDriveSource = { kind: "my_drive" };
export const SHARED_WITH_ME_SOURCE: GoogleDriveSource = {
  kind: "shared_with_me",
};

/** Serialise a source to the backend `source` query value. */
export function serializeGoogleDriveSource(source: GoogleDriveSource): string {
  switch (source.kind) {
    case "my_drive":
      return "my_drive";
    case "shared_with_me":
      return "shared_with_me";
    case "shared_drive":
      return `shared_drive:${source.id}`;
  }
}

interface Props {
  activeSource: GoogleDriveSource;
  setActiveSource: (source: GoogleDriveSource) => void;
  credentialId?: string;
}

/**
 * Sidebar source list for Google Drive. Renders three sections:
 *   1. My Drive
 *   2. Shared with me
 *   3. Shared drives — fetched from /integrations/gdrive/sources
 *
 * Mirrors the layout pattern of `SharepointSitePicker` (cascading site/drive
 * selectors) but adapted to Drive's flat source model.
 */
export const GoogleDriveSourcePicker = ({
  activeSource,
  setActiveSource,
  credentialId,
}: Props) => {
  const { spotGroup } = useSpotUser();

  const { data, isLoading } = useQuery({
    queryKey: ["gdrive-sources", spotGroup.id, credentialId],
    queryFn: () =>
      IntegrationsService.getGdriveSources({
        path: { group_id: spotGroup.id },
        query: credentialId ? { credential_id: credentialId } : undefined,
      }),
    staleTime: 60_000,
  });

  const sharedDrives = data?.data?.shared_drives ?? [];

  return (
    <div className="flex flex-col gap-1">
      <SourceItem
        icon={<HardDrive size={14} strokeWidth={1.5} />}
        label="My Drive"
        active={activeSource.kind === "my_drive"}
        onClick={() => setActiveSource(MY_DRIVE_SOURCE)}
      />
      <SourceItem
        icon={<Users size={14} strokeWidth={1.5} />}
        label="Shared with me"
        active={activeSource.kind === "shared_with_me"}
        onClick={() => setActiveSource(SHARED_WITH_ME_SOURCE)}
      />

      {(sharedDrives.length > 0 || isLoading) && (
        <div className="mt-3 flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground px-2">
            Shared drives
          </p>
          {isLoading && sharedDrives.length === 0 ? (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              Loading…
            </div>
          ) : (
            sharedDrives.map((drive) => {
              const isActive =
                activeSource.kind === "shared_drive" &&
                activeSource.id === drive.id;
              return (
                <SourceItem
                  key={drive.id}
                  icon={<Building2 size={14} strokeWidth={1.5} />}
                  label={drive.name}
                  active={isActive}
                  onClick={() =>
                    setActiveSource({
                      kind: "shared_drive",
                      id: drive.id,
                      name: drive.name,
                    })
                  }
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const SourceItem = ({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors",
      "hover:bg-muted",
      active
        ? "bg-muted font-semibold text-foreground"
        : "text-muted-foreground"
    )}
  >
    <span className="shrink-0">{icon}</span>
    <span className="truncate">{label}</span>
  </button>
);
